/**
 * Whether two roof faces describe the same plane in the same place.
 *
 * Several things are worked out for a roof face at generate time and stored
 * BY FACE INDEX: the provider's MCS irradiance and shade factor, our own
 * MGD 005 shade assessment with its sun path chart, the hourly sun access,
 * the photo verdict. Every one of them is a property of that plane at that
 * spot on the roof. Once the designer changes a face's pitch, azimuth or
 * outline, or reorders the faces, an index no longer names the plane those
 * figures were measured on. This is the test both sides apply before
 * carrying a stored figure across an edit: the server when it snapshots the
 * edited roof onto an estimate, the designer when it shows the MCS yield of
 * a face the installer is editing.
 *
 * Tolerances are deliberately tight. A face that came back from the client
 * untouched is float-identical (JSON round-trips a double exactly), so 5 cm
 * and a quarter of a degree only absorb rounding on the way through a UI
 * control, never a real edit. Anything looser would let a nudged face keep a
 * shade factor read from a horizon it no longer has.
 */

export type SolarRoofPlaneLatLng = { lat: number; lng: number };

export type SolarRoofPlaneLike = {
  polygon: {
    outer: ReadonlyArray<SolarRoofPlaneLatLng>;
    holes: ReadonlyArray<ReadonlyArray<SolarRoofPlaneLatLng>>;
  };
  pitchDegrees: number;
  azimuthDegrees: number;
};

export const SAME_ROOF_PLANE_VERTEX_TOLERANCE_M = 0.05;
export const SAME_ROOF_PLANE_ANGLE_TOLERANCE_DEG = 0.25;

const M_PER_DEG_LAT = 111_320;

const angleDifferenceDeg = (a: number, b: number): number => {
  const diff = Math.abs((((a - b) % 360) + 360) % 360);
  return Math.min(diff, 360 - diff);
};

const verticesCoincide = (
  a: SolarRoofPlaneLatLng,
  b: SolarRoofPlaneLatLng,
  mPerDegLng: number
): boolean => {
  const dx = (a.lng - b.lng) * mPerDegLng;
  const dy = (a.lat - b.lat) * M_PER_DEG_LAT;
  return Math.hypot(dx, dy) <= SAME_ROOF_PLANE_VERTEX_TOLERANCE_M;
};

/**
 * Two rings are the same outline when they have the same vertices in the
 * same winding order, allowing for a different starting vertex. A reversed
 * ring or a ring with a vertex added, removed or moved is a different
 * outline.
 */
const ringsCoincide = (
  a: ReadonlyArray<SolarRoofPlaneLatLng>,
  b: ReadonlyArray<SolarRoofPlaneLatLng>
): boolean => {
  if (a.length !== b.length) return false;
  if (a.length === 0) return true;
  const refLat = a.reduce((sum, point) => sum + point.lat, 0) / a.length;
  const mPerDegLng = M_PER_DEG_LAT * Math.cos((refLat * Math.PI) / 180);
  for (let offset = 0; offset < a.length; offset++) {
    let allMatch = true;
    for (let i = 0; i < a.length; i++) {
      if (!verticesCoincide(a[i], b[(i + offset) % b.length], mPerDegLng)) {
        allMatch = false;
        break;
      }
    }
    if (allMatch) return true;
  }
  return false;
};

export const sameRoofPlane = (
  a: SolarRoofPlaneLike,
  b: SolarRoofPlaneLike
): boolean => {
  if (
    Math.abs(a.pitchDegrees - b.pitchDegrees) >
    SAME_ROOF_PLANE_ANGLE_TOLERANCE_DEG
  ) {
    return false;
  }
  if (
    angleDifferenceDeg(a.azimuthDegrees, b.azimuthDegrees) >
    SAME_ROOF_PLANE_ANGLE_TOLERANCE_DEG
  ) {
    return false;
  }
  if (!ringsCoincide(a.polygon.outer, b.polygon.outer)) return false;
  if (a.polygon.holes.length !== b.polygon.holes.length) return false;
  return a.polygon.holes.every((hole, index) =>
    ringsCoincide(hole, b.polygon.holes[index])
  );
};

/**
 * For each edited face, the index of the original face it is the same plane
 * as, or null when it is a new or changed face. Each original face is
 * claimed at most once, so a duplicated face keeps its figures on one copy
 * only.
 */
export const matchEditedRoofFaces = (
  original: ReadonlyArray<SolarRoofPlaneLike>,
  edited: ReadonlyArray<SolarRoofPlaneLike>
): Array<number | null> => {
  const claimed = new Set<number>();
  return edited.map((face) => {
    for (let index = 0; index < original.length; index++) {
      if (claimed.has(index)) continue;
      if (sameRoofPlane(face, original[index])) {
        claimed.add(index);
        return index;
      }
    }
    return null;
  });
};
